<script setup lang="ts">
import {computed} from "vue";
import {storeToRefs} from "pinia";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import LoginUtils from "@/utils/login/LoginUtils.ts";
import dayjs from "@/utils/dayjs.ts";
import ChatUtils from "@/utils/common/ChatUtils.ts";
import ChatImageBubble from "@/components/navBar/chat/ChatImageBubble.vue";
import ChatVideoBubble from "@/components/navBar/chat/ChatVideoBubble.vue";
import ChatAudioBubble from "@/components/navBar/chat/ChatAudioBubble.vue";
import ChatMediaAlbum from "@/components/navBar/chat/ChatMediaAlbum.vue";
import ChatMessageTicks from "@/components/navBar/chat/ChatMessageTicks.vue";
import {MessageMetadata} from "@/utils/presenters/interfaces.ts";
import ChatMessageType from "@/utils/constants/ChatMessageType.ts";

const props = defineProps<{
  message: MessageMetadata;
  albumMessages?: MessageMetadata[];
  chatType: string;
  currentUserId?: string;
}>();

const emit = defineEmits<{
  preview: [src: string, fileName: string];
  previewAlbum: [messages: MessageMetadata[], startIndex: number];
  openUserChat: [userId: string];
}>();

const {darkMode} = storeToRefs(useNamaFlagsStore());

const isAlbum = computed(() => !!props.albumMessages && props.albumMessages.length >= 2);
const sent = computed(() => props.message.sender?.id === props.currentUserId);
const stamp = computed(() => dayjs(props.message.sentAt).format('HH:mm'));
const albumStamp = computed(() => {
  if (!props.albumMessages?.length) return stamp.value;
  const last = props.albumMessages[props.albumMessages.length - 1];
  return dayjs(last.sentAt).format('HH:mm');
});
const status = computed(() => {
  if (props.albumMessages?.length)
    return props.albumMessages[props.albumMessages.length - 1].actualStatus;
  return props.message.actualStatus;
});
const textColor = computed(() => (sent.value || darkMode.value) ? 'white' : 'black');
const bgColor = computed(() => sent.value ? 'primary' : (darkMode.value ? 'grey-9' : 'grey-4'));
const groupName = computed(() => props.chatType === 'Group' && props.message.sender?.id !== props.currentUserId ? (props.message.sender?.name1 ?? '') : undefined);
const groupAvatar = computed(() => props.chatType === 'Group' && props.message.sender?.id !== props.currentUserId ? (LoginUtils.getUserImageSrc(props.message.sender?.id ?? '', false) ?? '') : undefined);
const messageType = computed<ChatMessageType>(() => {
  if (isAlbum.value)
    return 'Image'; // albums are always image/video
  return ChatUtils.resolveEffectiveMessageType(props.message);
});
const isFile = computed(() => messageType.value === 'File' && !!props.message.attachment?.attachmentInfo);
const downloadUrl = computed(() => ChatUtils.getFileDownloadUrl(props.message.attachment?.attachmentInfo?.fileId));

const isSystemMsg = computed(() => !!props.message.isSystemMessage);
const systemMessageLabel = computed(() => {
  if (!isSystemMsg.value || !props.message.messageContent)
    return '';
  return ChatUtils.getSystemMessagePreview(props.message.messageContent, props.currentUserId);
});

const hasMentions = computed(() => {
  if (!props.message.messageContent) return false;
  ChatUtils.MENTION_REGEX.lastIndex = 0;
  return ChatUtils.MENTION_REGEX.test(props.message.messageContent);
});

function onMentionClick(e: MouseEvent) {
  const target = (e.target as HTMLElement).closest('.chat-mention-link') as HTMLAnchorElement | null;
  if (!target) return;
  e.preventDefault();
  const href = target.getAttribute('href');
  if (!href) return;
  if (href.startsWith('user:')) {
    emit('openUserChat', href.substring(5));
  } else {
    window.open(href, '_blank');
  }
}

function onPreview(src: string, fileName: string) {
  emit('preview', src, fileName);
}

function onPreviewAlbum(messages: MessageMetadata[], startIndex: number) {
  emit('previewAlbum', messages, startIndex);
}

</script>

<template>
  <div v-if="isSystemMsg" class="tw-flex tw-justify-center tw-my-2">
    <span class="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400 tw-bg-gray-100 dark:tw-bg-gray-800
                 tw-px-3 tw-py-1 tw-rounded-full tw-max-w-[80%] tw-text-center"
          v-text="systemMessageLabel"/>
  </div>
  <q-chat-message v-else-if="isAlbum"
                  :sent="sent" :name="groupName" :avatar="groupAvatar"
                  :text-color="textColor" :bg-color="bgColor"
                  class="nama-chat-message nama-chat-message-avatar nama-chat-message-name">
    <ChatMediaAlbum :messages="albumMessages!" :sent="sent" :stamp="albumStamp"
                    :status="status" :dark-mode="darkMode" @preview-album="onPreviewAlbum"/>
  </q-chat-message>
  <q-chat-message v-else-if="messageType === 'Image'"
                  :sent="sent" :name="groupName" :avatar="groupAvatar"
                  :text-color="textColor" :bg-color="bgColor"
                  class="nama-chat-message nama-chat-message-avatar nama-chat-message-name">
    <ChatImageBubble :message="message" :sent="sent" :stamp="stamp"
                     :status="status" :dark-mode="darkMode" @preview="onPreview"/>
  </q-chat-message>
  <q-chat-message v-else-if="messageType === 'Video'"
                  :sent="sent" :name="groupName" :avatar="groupAvatar"
                  :text-color="textColor" :bg-color="bgColor"
                  class="nama-chat-message nama-chat-message-avatar nama-chat-message-name">
    <ChatVideoBubble :message="message" :sent="sent" :stamp="stamp"
                     :status="status" :dark-mode="darkMode" @preview="onPreview"/>
  </q-chat-message>
  <q-chat-message v-else-if="messageType === 'Audio'"
                  :sent="sent" :name="groupName" :avatar="groupAvatar"
                  :text-color="textColor" :bg-color="bgColor"
                  class="nama-chat-message nama-chat-message-avatar nama-chat-message-name">
    <ChatAudioBubble :message="message" :sent="sent" :stamp="stamp" :status="status" :dark-mode="darkMode"/>
  </q-chat-message>
  <q-chat-message v-else-if="isFile"
                  :sent="sent" :name="groupName" :avatar="groupAvatar"
                  :stamp="stamp" :text-color="textColor" :bg-color="bgColor"
                  class="nama-chat-message nama-chat-message-avatar nama-chat-message-name">
    <div class="tw-w-full">
      <a :href="downloadUrl"
         class="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-rounded-lg tw-no-underline tw-min-w-48
                tw-bg-black/20 dark:tw-bg-white/20 hover:tw-bg-black/25 dark:hover:tw-bg-white/25 tw-transition-colors"
         :class="sent ? 'tw-text-white' : (darkMode ? 'tw-text-white' : 'tw-text-gray-800')">
        <q-icon :name="ChatUtils.getFileIcon(message.attachment?.attachmentInfo?.fileName ?? '')" size="2rem"
                :color="textColor" class="tw-shrink-0"/>
        <div class="tw-flex tw-flex-col tw-overflow-hidden tw-flex-1 tw-w-36" :title="message.attachment?.attachmentInfo?.fileName">
          <span class="tw-text-sm tw-font-medium tw-truncate" v-text="message.attachment?.attachmentInfo?.fileName"/>
          <span class="tw-text-xs tw-opacity-70" v-text="ChatUtils.formatFileSize(message.attachment?.dataLength ?? 0)"/>
        </div>
        <q-icon name="download" size="1.2rem" class="tw-shrink-0 tw-opacity-70" :color="textColor"/>
      </a>
      <div v-if="message.messageContent?.trim()" class="tw-mt-1 tw-text-sm" v-text="message.messageContent"/>
    </div>
    <template v-if="sent" #stamp>
      <span class="tw-inline-flex tw-items-center tw-gap-1">
        <span v-text="stamp"/>
        <ChatMessageTicks :status="status"/>
      </span>
    </template>
  </q-chat-message>
  <q-chat-message v-else-if="hasMentions"
                  :sent="sent" :name="groupName" :avatar="groupAvatar"
                  :stamp="stamp" :text-color="textColor" :bg-color="bgColor"
                  :size="(ChatUtils.getDisplayTextLength(message.messageContent) ?? 0) > 80 ? '10' : ''"
                  class="nama-chat-message-avatar nama-chat-message-name">
    <span v-html="ChatUtils.renderMentions(message.messageContent!, sent)" class="tw-w-[inherit]" @click="onMentionClick"/>
    <template v-if="sent" #stamp>
      <span class="tw-inline-flex tw-items-center tw-gap-1">
        <span v-text="stamp"/>
        <ChatMessageTicks :status="status"/>
      </span>
    </template>
  </q-chat-message>
  <q-chat-message v-else
                  :sent="sent" :name="groupName" :avatar="groupAvatar"
                  :text="[message.messageContent]"
                  :stamp="stamp" :text-color="textColor" :bg-color="bgColor"
                  :size="(message.messageContent?.length ?? 0) > 80 ? '10' : ''"
                  class="nama-chat-message-avatar nama-chat-message-name">
    <template v-if="sent" #stamp>
      <span class="tw-inline-flex tw-items-center tw-gap-1">
        <span v-text="stamp"/>
        <ChatMessageTicks :status="status"/>
      </span>
    </template>
  </q-chat-message>
</template>
